import type { UserType } from "../contracts/permissions.js";

const MAX_RETURN_PATH_LENGTH = 1024;
const RETURN_ORIGIN = "https://return.invalid";
const ALLOWED_RETURN_PATH = /^[A-Za-z0-9/?&=%._~-]+$/;

function isProtectedPath(pathname: string): boolean {
  return (
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname === "/portal" ||
    pathname.startsWith("/portal/")
  );
}

function isAuthPage(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname === "/logout" ||
    pathname.startsWith("/logout/")
  );
}

/**
 * Same-origin return path under /admin or /portal.
 * Decodes nothing: callers must pass the value URLSearchParams already decoded once.
 * Returns null for anything that could leave the app or bounce back into login/logout.
 */
export function safeReturnPath(input: unknown): string | null {
  if (typeof input !== "string") return null;
  if (input.length === 0 || input.length > MAX_RETURN_PATH_LENGTH) return null;
  if (/[\u0000-\u0020\u007f\\@]/.test(input)) return null;
  if (!input.startsWith("/") || input.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(input)) return null;
  if (input.includes("//")) return null;

  let url: URL;
  try {
    url = new URL(input, RETURN_ORIGIN);
  } catch {
    return null;
  }

  if (url.origin !== RETURN_ORIGIN) return null;
  if (url.username || url.password) return null;

  const hashAt = input.indexOf("#");
  const withoutHash = hashAt === -1 ? input : input.slice(0, hashAt);
  const kept = url.pathname + url.search;
  if (kept !== withoutHash) return null;
  if (!isProtectedPath(url.pathname) || isAuthPage(url.pathname)) return null;
  if (!ALLOWED_RETURN_PATH.test(kept)) return null;

  return kept;
}

export function loginUrlForProtectedPath(
  pathname: string,
  search: string,
): string | null {
  if (!isProtectedPath(pathname)) return null;
  const safe = safeReturnPath(`${pathname}${search}`);
  if (!safe) return "/login";
  return `/login?returnTo=${encodeURIComponent(safe)}`;
}

export function mismatchRedirect(
  userType: UserType,
  path: string,
): "/admin" | "/portal" | null {
  const pathname = path.split("?")[0] ?? path;
  if (
    userType === "client" &&
    (pathname === "/admin" || pathname.startsWith("/admin/"))
  ) {
    return "/portal";
  }
  if (
    (userType === "admin" || userType === "agent") &&
    (pathname === "/portal" || pathname.startsWith("/portal/"))
  ) {
    return "/admin";
  }
  return null;
}

export function postLoginPath(returnTo: string | null | undefined): string {
  return safeReturnPath(returnTo) ?? "/";
}
